'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SubscriptionsManager, UserSubscription } from '@benzinga/subscription-manager';
import { SubscriptionRow } from './SubscriptionRow';
import { countRows } from './subscriptions';
import type { SubscriptionGroup, SubscriptionGroupId } from './subscriptions';
import styles from './Billing.module.css';

const GROUP_COPY: Record<SubscriptionGroupId, { key: string; label: string }> = {
  active: { key: 'Billing.group-active', label: 'Active' },
  past: { key: 'Billing.group-past', label: 'Past plans' },
  trialing: { key: 'Billing.group-trialing', label: 'Trials' },
};

interface SubscriptionsCardProps {
  groups: SubscriptionGroup[];
  isLoading: boolean;
  setSubToCancel: (sub: UserSubscription | null) => void;
  subscriptionManager: SubscriptionsManager;
}

export function SubscriptionsCard({ groups, isLoading, setSubToCancel, subscriptionManager }: SubscriptionsCardProps) {
  const { t } = useTranslation(['profile', 'common']);
  const total = countRows(groups);
  const hasAutoRenewRow = groups.some(group => group.rows.some(row => row.autoRenew !== null));

  return (
    <div className="acct-card">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>
          {t('Billing.subscriptions', { defaultValue: 'Subscriptions', ns: 'profile' })}
          {total > 0 ? ` (${total})` : ''}
        </h3>
      </div>

      {isLoading ? (
        <div className={styles.rows}>
          {[0, 1].map(index => (
            <div className={styles.subRow} key={index}>
              <div>
                <div aria-hidden="true" className={styles.skeleton} style={{ width: index === 0 ? '55%' : '40%' }} />
                <div aria-hidden="true" className={styles.skeletonMeta} />
              </div>
            </div>
          ))}
        </div>
      ) : total === 0 ? (
        <p className={styles.empty}>
          {t('Billing.noactive-subscription', { defaultValue: 'You have no active subscriptions.', ns: 'profile' })}
        </p>
      ) : (
        <>
          {groups.map(group => (
            <React.Fragment key={group.id}>
              <div className={styles.groupTitle}>
                {t(GROUP_COPY[group.id].key, { defaultValue: GROUP_COPY[group.id].label, ns: 'profile' })} (
                {group.rows.length})
              </div>
              <div className={styles.rows}>
                {group.rows.map(row => (
                  <SubscriptionRow
                    key={row.subscription.uuid}
                    row={row}
                    setSubToCancel={setSubToCancel}
                    subscriptionManager={subscriptionManager}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
          {hasAutoRenewRow ? (
            <p className={styles.note}>
              {t('Billing.cancel-hint', {
                defaultValue:
                  'To cancel a subscription, switch Auto-renew off — it stays active until the end of the billing period.',
                ns: 'profile',
              })}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
